import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowLeft, Twitter, Linkedin, MessageSquare, Youtube, User } from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';
import ThemeToggle from './ThemeToggle';

interface StaticPageLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
  fullWidth?: boolean;
}

export default function StaticPageLayout({
  children,
  title,
  subtitle,
  showBackButton = true,
  fullWidth = false
}: StaticPageLayoutProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const { isAuthenticated, user } = useAuthStore();

  // Scroll to top on page load/reload
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b ${
        isDark ? 'bg-dark-900/80 border-white/5' : 'bg-white/80 border-slate-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-purple-500/25"
              >
                <Sparkles className="w-6 h-6 text-white" />
              </motion.div>
              <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot</span>
            </Link>

            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow">
                    {user?.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-white">
                        {user?.name?.charAt(0).toUpperCase() || <User className="w-5 h-5 text-white" />}
                      </span>
                    )}
                  </div>
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`hidden sm:block transition-colors px-4 py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 sm:px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow text-sm sm:text-base"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${fullWidth ? 'max-w-[1400px]' : 'max-w-4xl'}`}>
          {showBackButton && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-8"
            >
              <Link
                to="/"
                className={`inline-flex items-center gap-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className={`text-2xl sm:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h1>
            {subtitle && (
              <p className={`text-base sm:text-xl mb-8 sm:mb-12 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{subtitle}</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            {children}
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Logo */}
            <div className="col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot</span>
              </Link>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                AI employees that never sleep. Build, grow, and scale your
                business 24/7.
              </p>
              <div className="flex space-x-3">
                {[
                  { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/flowai' },
                  { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/flowai' },
                  { name: 'Discord', icon: MessageSquare, href: 'https://discord.gg/flowai' },
                  { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@flowai' },
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isDark
                        ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                        : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                    }`}
                  >
                    <span className="sr-only">{social.name}</span>
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Links */}
            {[
              {
                title: 'Product',
                links: [
                  { name: 'AI Employees', href: '/employees' },
                  { name: 'Features', href: '/features' },
                  { name: 'Pricing', href: '/pricing' },
                  { name: 'Integrations', href: '/integrations' },
                  { name: 'API', href: '/api' },
                ],
              },
              {
                title: 'Company',
                links: [
                  { name: 'About', href: '/about' },
                  { name: 'Blog', href: '/blog' },
                  { name: 'Careers', href: '/careers' },
                  { name: 'Press', href: '/press' },
                  { name: 'Contact', href: '/contact' },
                ],
              },
              {
                title: 'Resources',
                links: [
                  { name: 'Documentation', href: '/docs' },
                  { name: 'Help Center', href: '/help' },
                  { name: 'Community', href: '/community' },
                  { name: 'Templates', href: '/templates' },
                  { name: 'Roadmap', href: '/roadmap' },
                  { name: 'Status', href: '/status' },
                ],
              },
              {
                title: 'Legal',
                links: [
                  { name: 'Privacy', href: '/privacy' },
                  { name: 'Terms', href: '/terms' },
                  { name: 'Security', href: '/security' },
                  { name: 'Cookies', href: '/cookies' },
                  { name: 'GDPR', href: '/gdpr' },
                  { name: 'Refund Policy', href: '/refund-policy' },
                ],
              },
            ].map((column) => (
              <div key={column.title}>
                <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                <ul className="space-y-3">
                  {column.links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
            <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
              © 2024 SweetPilot. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                Privacy Policy
              </Link>
              <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                Terms of Service
              </Link>
              <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
